import { useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchCombobox } from "@/components/ui/search-combobox";
import { SelectDeviceCard } from "@/components/select-device-card";
import { searchUsers } from "@/lib/snipeit/users";
import { proxyImageUrl } from "@/lib/snipeit/client";
import type { SnipeItAsset, SnipeItLocation, SnipeItUser } from "@/lib/snipeit/types";
import { Input } from "@/components/ui/input";
import { useLocationDetail } from "@/common/api/use-location-detail";
import { Link } from "wouter";
import { VERWERTER_STATUS } from "@/common/constants";
import { useCheckoutHardware } from "@/common/api/use-checkout-hardware";

// Kept for unit tests and any future programmatic use
const EXCLUDED_STATUSES = new Set([VERWERTER_STATUS]);

export function isAvailableForCheckout(asset: SnipeItAsset, location: SnipeItLocation | null): boolean {
  if (asset.status_label?.status_meta !== "deployable") return false;
  if (EXCLUDED_STATUSES.has(asset.status_label?.name ?? "")) return false;
  if (asset.assigned_to) return false;
  if (location && asset.location && asset.location.id !== location.id) return false;
  return true;
}

export function CheckoutDeviceView() {
  const { location: savedLocation } = useLocationDetail();
  const [selected, setSelected] = useState<SnipeItAsset | null>(null);
  const [selectedUser, setSelectedUser] = useState<SnipeItUser | null>(null);
  const [userInput, setUserInput] = useState("");
  const [assetTag, setAssetTag] = useState("");

  const imageUrl = proxyImageUrl(selected?.image);

  const checkoutMutation = useCheckoutHardware();

  const isLoading = checkoutMutation.isPending;

  return (
    <main className="bg-background flex min-h-dvh items-start justify-center overflow-y-auto px-4 pt-6 pb-6 md:h-dvh md:overflow-hidden">
      <div className="flex w-full max-w-3xl flex-col gap-3 md:h-full">
        {!savedLocation && (
          <div className="border-border bg-card rounded-xl border p-6 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              Bitte zuerst einen Standort in den{" "}
              <Link href="/settings" className="text-primary underline">
                Einstellungen
              </Link>{" "}
              festlegen.
            </p>
          </div>
        )}

        {savedLocation && (
          <div className="flex h-full w-full flex-col items-start gap-6 md:flex-row">
            <div className="w-full shrink-0 space-y-3 md:w-80">
              <SelectDeviceCard
                queryKeyPrefix="checkout"
                locationId={savedLocation?.id}
                statusType="deployable"
                onSelect={(asset) => {
                  setSelected(asset);
                  setAssetTag(asset.asset_tag ?? "");
                  checkoutMutation.reset();
                }}
              />

              {checkoutMutation.isError && (
                <p className="text-destructive text-sm">
                  {checkoutMutation.error instanceof Error ? checkoutMutation.error.message : "Unbekannter Fehler"}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link href="/" className={buttonVariants({ variant: "secondary", size: "sm" })}>
                  Zurück
                </Link>
                <Button
                  size="sm"
                  className="w-full md:w-auto"
                  disabled={!selected || !selectedUser || isLoading}
                  onClick={() => {
                    if (!selected || !selectedUser) return;
                    const tagChanged = assetTag !== (selected.asset_tag ?? "");
                    checkoutMutation.mutate({
                      assetId: selected.id,
                      userId: selectedUser.id,
                      newAssetTag: tagChanged ? assetTag : undefined,
                    });
                  }}
                >
                  {isLoading ? "Wird verarbeitet…" : "Herausgeben"}
                </Button>
              </div>
            </div>

            <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-3">
              {selected && (
                <Card className="border-border shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-muted-foreground text-sm font-medium">Neuer Asset Tag</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input value={assetTag} onChange={(e) => setAssetTag(e.target.value)} placeholder="Asset Tag" />
                  </CardContent>
                </Card>
              )}

              <Card className="border-border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-sm font-medium">Person</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <label className="text-muted-foreground text-xs font-medium">Name</label>
                    <SearchCombobox
                      placeholder="Person suchen…"
                      queryKey="users"
                      searchFn={(query) =>
                        searchUsers(query).then((results) =>
                          results.map((user) => ({
                            id: user.id,
                            label: user.name,
                            sublabel: user.username,
                            data: user,
                          })),
                        )
                      }
                      value={userInput}
                      onInputChange={(value) => {
                        setUserInput(value);
                        if (selectedUser && value !== selectedUser.name) {
                          setSelectedUser(null);
                          checkoutMutation.reset();
                        }
                      }}
                      onSelect={(result) => {
                        const user = result.data as SnipeItUser;
                        setSelectedUser(user);
                        setUserInput(user.name);
                        checkoutMutation.reset();
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              {imageUrl && (
                <div className="border-border bg-card max-h-[40dvh] w-full shrink overflow-hidden rounded-xl border shadow-sm md:max-h-none md:min-h-0 md:basis-[80%]">
                  <img
                    src={imageUrl}
                    alt={selected?.model?.name ?? selected?.name}
                    className="h-full w-full object-contain p-4"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
